import {
	Backdrop,
	Box,
	Button,
	Theme,
	Typography,
	useTheme
} from "@mui/material";
import { observer } from "mobx-react";
import React from "react";
import { useTranslation } from 'react-i18next';
import { Document, Page } from 'react-pdf';
import useEmotionStyles from "@styles/shared/useEmotionStyles";
import { CompareDataStyle } from "@styles/assignments/CompareDataStyle";

import { useStores } from "@hooks/useStores";
import useNeoPen from "../../../../ndp/hooks/useNeoPen";
import { codeToRecognizedTextPromise } from "../../../../ndp/hooks/useStrokeRecognition";
import { ProjectExamType } from "@repositories/model/support/ProjectExamType";

import { CircularProgress } from "@mui/material";
import { makePdfWithStrokes } from "../../../../ndp/NdpPdfPrinter";
import { getBrowserAndDeviceInfo, IPageSOBP, NeoStroke, OperatingSystemInfo } from "@neolab-libs/nl-lib3-common";
import { IProjectSubmissionTransfer } from "@repositories/model/transfer/IProjectSubmissionTransfer";
import NcodeIsNotAllocationDialog from "../OfflineSubmissions/PenDataUploaderDialog/NcodeIsNotAllocationDialog";
import OfflineDataUploadDialog, { RequestOfflineDataState } from "../OfflineSubmissions/PenDataUploaderDialog/OfflineDataUploadDialog";
import { useNeoConfirm } from "@hooks/useNeoConfirm";
import CompareOcrDataStudent from "./CompareOcrDataStudent";
import { USBStrokeUploader } from "@/views/assignments/manage/OfflineSubmissions/CompareBatchNeoPenData/USB/Uploader/USBStrokeUploader";
import { USBCompareDialogParams } from "../OfflineSubmissions/CompareBatchNeoPenData/USB/USBPenInfo";

let a = 0;
type Props = {
	handleClickClose: (usbCompareDialogParams?: USBCompareDialogParams) => void;
	projectCode: string;
	templateProjectCode: string;
	submissionTransfer: IProjectSubmissionTransfer & { submissionIndex: number };
	noShowPenDialog: boolean,

	// batch upload에서 사용
	givenData?: { numPages: number; numStrokes: number; strokes: NeoStroke[]; }
	// submissionCode: string;
	isForComparisonOnly: boolean,
	isForUSBDataConfirm?: boolean,
	usbCompareDialogParams?: USBCompareDialogParams
};

function CompareNeoPenDataStudent(props: Props) {
	const i18next = useTranslation();
	const classes = useEmotionStyles(CompareDataStyle);
	const { projectCode, templateProjectCode, submissionTransfer, givenData, isForComparisonOnly, isForUSBDataConfirm, usbCompareDialogParams } = props;
	const { navigateStore, projectStore, projectExamStore, neoPenStore, ncodeAllocationStore, authStore, projectSubmissionStore, projectSubmissionAnswerStore } = useStores();
	const { project } = projectStore;

	const [page, setPage] = React.useState(0);
	const [pages, setPages] = React.useState([]);
	const [pdfWidth, setPdfWidth] = React.useState(0);
	const [pdfWithStroke, setPdfWithStrokeBlobUrl] = React.useState(null);
	const [handwritingConfirmed, setHandwritingConfirmed] = React.useState(false);
	const [notAllocatedDlgState, setNotAllocatedDlgState] = React.useState<"none" | "notAllocated" | "confirmed" | "allocated">("none");
	const [requestOfflineDataState, setRequestOfflineDataState] = React.useState(RequestOfflineDataState.disconnected);
	const [openUploadDlg, setOpenUploadDlg] = React.useState(true);
	const [allocationRange, setAllocationRange] = React.useState<{ start: IPageSOBP, end: IPageSOBP }>(null);
	const [strokesDataReady, setStrokesDataReady] = React.useState(true);

	const [isWindowsOS, setIsWindowsOS] = React.useState(true);
	
	const [offlineStrokes, _setOfflineStrokes] = React.useState<NeoStroke[]>(null);
	const setOfflineStrokes = (val: NeoStroke[]) => {
		_setOfflineStrokes(val);

		console.log("CompareNeoPenData", projectCode, templateProjectCode, submissionTransfer);
	}

	React.useEffect(() => {
		setOfflineStrokes(givenData?.strokes || null);
	}, [givenData]);

	const pdfBoxRef = React.useRef<HTMLElement>();
	const { submissionCode, userCode } = submissionTransfer || {};

	const [submittedMaterial, setSubmittedMaterial] = React.useState<"pdf" | "stroke" | "none">("none");
	const theme = useTheme();
	const [isUSBModeUploadingData, setIsUSBModeUploadingData] = React.useState(false);
	const usbModeUploaderRef = React.useRef<USBStrokeUploader | null>(null);

	const { pdfBlobURL, pageCount, ncodeAllocation } = useNeoPen({
		project,
		projectStore,
		projectYear: project?.year,
		projectSemester: project?.semesterType,
		examList: projectExamStore.projectExamList,
		neoPenStore,
		ncodeAllocationStore,
		lang: navigateStore.language,
		authStore
	});


	const getProjectInformation = async () => {
		console.log(`getProjectInformation ${++a}`);
		const promises = [];

		if ((submissionTransfer?.files?.length || 0) > 0) {
			const strokeFiles = submissionTransfer.files.filter((item) => item.downloadUrl.endsWith(".zip") || item.contentsType.toUpperCase() === "STRK_ZIP");

			if (strokeFiles.length > 0) {
				setSubmittedMaterial("stroke");
				setStrokesDataReady(false);
				const pr5 = projectSubmissionStore.downloadStrokesFromServer(submissionTransfer)
					.then((strokes) => {
						setOfflineStrokes(strokes);
						setStrokesDataReady(true);
					})
					.catch((e) => {
						console.log(e);
						setStrokesDataReady(true);
					});
				promises.push(pr5);
			}
		}

		const pr1 = projectStore.downloadProjectInfoAndStore(projectCode); //기본정보 %필수정보
		promises.push(pr1);
		const pr2 = projectExamStore.downloadProjectExamListAndStore(projectCode); // 문항 생성 %필수 정보
		promises.push(pr2);

		if (submissionCode) {
			const pr3 = projectSubmissionStore.downloadSubmissionTransferAndStore(projectCode, submissionCode);
			promises.push(pr3);
			const pr4 = projectSubmissionAnswerStore.downloadSubmissionAnswersAndStore(submissionCode);
			promises.push(pr4);
		}

		await Promise.all(promises);
	}

	const loadErr = (e) => {
		console.log(i18next.t('문서를 로드하는 동안 오류가 발생했습니다!'), e.message);
	}

	const loadSuccess = (args: { numPages: number }) => {
		const { numPages } = args;

		let tempArr: number[] = [];
		for (let i = 0; i < numPages; i++) {
			tempArr.push(i + 1);
		}
		setPage(1);
		setPages(tempArr);
		setPdfWidth(pdfBoxRef?.current?.offsetWidth);
	}


	const handleResize = () => {
		setPdfWidth(pdfBoxRef.current?.offsetWidth);
	}

	const handleClickClose = async () => {
		neoPenStore.setSortedOfflineStrokes([]);
		Object.keys(codeToRecognizedTextPromise).forEach(key => delete codeToRecognizedTextPromise[key]);

		props.handleClickClose();
	}

	const { confirm } = useNeoConfirm();

	// 펜의 offline data를 업로드하고 펜 내의 데이터를 지우는 곳
	const confirmHandwritingData = async () => {
		const yesNo = await confirm(
			i18next.t('확인을 누르면 현재 보고 있는 데이터는 펜에서 완전히 삭제되고 다음의 처리로 진행됩니다. 계속하시겠습니까?'),
			i18next.t('필기 데이터 삭제 확인'),
		);

		if (!yesNo) return;

		// 2025-09-08 usb로 데이터 업로드하는 경우에 처리 방법이 다름.
		if (isForUSBDataConfirm == true) {
			setIsUSBModeUploadingData(true);

			// instantiate usbModeUploaderRef
			if (usbCompareDialogParams.deviceId && usbCompareDialogParams.fileNames) {
				usbModeUploaderRef.current = new USBStrokeUploader(projectCode, userCode, allocationRange, offlineStrokes, submissionCode, projectSubmissionStore, usbCompareDialogParams.deviceId, usbCompareDialogParams.fileNames);
				await usbModeUploaderRef.current.uploadAndDelete();
			}

			setIsUSBModeUploadingData(false);

			// close the popup
			neoPenStore.setSortedOfflineStrokes([]);
			Object.keys(codeToRecognizedTextPromise).forEach(key => delete codeToRecognizedTextPromise[key]);

			// set flag to mark that usb mode uploading is finished
			props.handleClickClose(usbCompareDialogParams);

			return;
		}

		const code = await projectSubmissionStore.uploadAnswerStrokes({
			projectCode,
			userCode,
			range: allocationRange,
			strokes: offlineStrokes,
			submissionCode
		});

		if (code) {
			await neoPenStore.removeOfflineData({ range: allocationRange });
			setHandwritingConfirmed(true);
		}

		// 2025-04-03 윈도우즈가 아니면, 창 닫는걸로.
		if (isStrokeRecognitionPossible == false) {
			neoPenStore.setSortedOfflineStrokes([]);
			Object.keys(codeToRecognizedTextPromise).forEach(key => delete codeToRecognizedTextPromise[key]);
			props.handleClickClose();
		}
	}


	const handleCloseNotAllocatedDlg = () => {
		setNotAllocatedDlgState("confirmed");
		handleClickClose();

	}

	const retryUploadOfflinestroke = () => {
		setRequestOfflineDataState(RequestOfflineDataState.disconnected);
		setOpenUploadDlg(true);
	}

	React.useEffect(() => {
		window.addEventListener('resize', handleResize);
		// storeManager.changeStoreState(StoreStatus.PROGRESS);

		getProjectInformation()
			.then(() => {
				// storeManager.changeStoreState(StoreStatus.COMPLETED);
				navigateStore.changeWithoutBars(true);
			})
			.catch((error) => {
				// storeManager.changeStoreState(StoreStatus.COMPLETED);
			});

		// 2025-04-03 윈도우즈인지 아닌지 판단. 윈도우즈가 아니라면, 필기인식을 못하고, 오른쪽 화면이 나와서는 안되기 때문에. 그 판단 용도로 쓰이는 변수
		const info = getBrowserAndDeviceInfo();
		const isWindows = info.os == OperatingSystemInfo.Windows;
		setIsWindowsOS(isWindows);

		// 2025-04-15 만약 '대조하기' 단계에서 이 페이지를 띄웠다면, 오른쪽 화면으로 바로 넘어가기
		const isFileExists = ((submissionTransfer?.files?.length || 0) > 0);
		if (isFileExists && isForComparisonOnly) {
			setHandwritingConfirmed(true);
		}

		return () => {
			window.removeEventListener('resize', handleResize);
			neoPenStore.disconnectPen();
			navigateStore.changeWithoutBars(false);
			projectSubmissionAnswerStore.initSubmissionAnswer();
		}
	}, []);



	React.useEffect(() => {
		if (pageCount === null) return;
		if (ncodeAllocation) {
			setNotAllocatedDlgState("allocated");
			const { section, startOwner, startBook, startPage, endOwner, endBook, endPage } = ncodeAllocation;
			const range: { start: IPageSOBP, end: IPageSOBP } = {
				start: { section, owner: startOwner, book: startBook, page: startPage },
				end: { section, owner: endOwner, book: endBook, page: endPage }
			};
			setAllocationRange(range);
		}
		else {
			setNotAllocatedDlgState("notAllocated");
		}
	}, [pageCount, ncodeAllocation]);


	// offline stroke가 offline data upload dialog에서 upload 되면
	React.useEffect(() => {
		const generatePdf = async (strokes: NeoStroke[], range: { start: IPageSOBP, end: IPageSOBP }) => {
			setStrokesDataReady(false);
			console.log("render", strokes, range);
			try {
				const pdfUrl = await makePdfWithStrokes({
					type: projectExamStore.projectExamList[0]?.type || ProjectExamType.DESCRIPTIVE,
					lang: navigateStore.language,
					ndpNcodeRange: {
						section: range.start.section,
						owner: range.start.owner,
						bookCode: range.start.book,
						pageStart: range.start.page,
						pageEnd: range.end.page
					},
					strokes,
					dpi: 120,
				});
				setPdfWithStrokeBlobUrl(pdfUrl);
				const blob = await fetch(pdfUrl).then(r => r.blob());
				// saveAs(new Blob([blob]), "test.pdf");
			} finally {
				setStrokesDataReady(true);
			}
		}

		if (ncodeAllocation && offlineStrokes) {
			const { section, startOwner, startBook, startPage, endOwner, endBook, endPage } = ncodeAllocation;
			const range: { start: IPageSOBP, end: IPageSOBP } = {
				start: { section, owner: startOwner, book: startBook, page: startPage },
				end: { section, owner: endOwner, book: endBook, page: endPage }
			};

			generatePdf(offlineStrokes, range);
		}

	}, [ncodeAllocation, offlineStrokes]);

	
	// 2025-04-09 필기인식이 가능하냐 하지 않냐 판단하는 변수
	const isStrokeRecognitionPossible = React.useMemo(() => {

		// 2025.06.26 - 학생이면 아예 불가능한 것으로 취급하기 위해서 변경
		// return isWindowsOS === true;
		return false;
	}, [isWindowsOS]);

	// const pdfFileUrl = submittedMaterial === "stroke" ? pdfWithStroke : pdfBlobURL || pdfWithStroke;

	const pdfFileUrl = pdfWithStroke || pdfBlobURL;

	return (
		<div className={classes.root}>
			<Box className={classes.padding} >
				<Box className={classes.mainContentBox}>
					<Box className={isStrokeRecognitionPossible == true ? classes.newTabContentHorizontalFlow : classes.newTabContentHorizontalCenter} display='flex' alignItems='center' >
						<Box className={classes.fileUploadBox} sx={{ marginRight: "10px" }}>
							<Box display='flex' alignItems='center' justifyContent='space-between' className={classes.compareTop}>
								<Typography>{i18next.t('필기원본')}</Typography>
								<Box display='flex' alignItems='center'>
									{/* <Typography style={{ marginRight: "10px" }} className={classes.compareText}>{i18next.t('다른 학생의 필기입니까?')}</Typography> */}
									<Button className={classes.selectOtherStudentBtnSmall} onClick={handleClickClose} disableRipple disabled={false}>
										<Typography>{i18next.t('닫기')}</Typography>
									</Button>
								</Box>


								<Box display='flex' alignItems='center'>
									{/* <Typography style={{ marginRight: "10px" }} className={classes.compareText}>{i18next.t('다른 학생의 필기입니까?')}</Typography> */}
									<Button
										className={classes.compareTopBtn}
										onClick={retryUploadOfflinestroke}
										disableRipple
										disabled={openUploadDlg || handwritingConfirmed}
									>
										<Typography>{i18next.t('다시 전송')}</Typography>
									</Button>
								</Box>


								<Box display='flex' alignItems='center'>
									{/* <Typography style={{ marginRight: "10px" }} className={classes.compareText}>{i18next.t('다른 학생의 필기입니까?')}</Typography> */}
									<Button className={classes.confirmHandwritingDataBtn} onClick={confirmHandwritingData} disableRipple disabled={!submissionTransfer ||
										(handwritingConfirmed ? true : false) || !strokesDataReady || !(pdfWithStroke || pdfBlobURL)}>
										<Typography>{i18next.t('필기 데이터 확인 완료 ▷▷▷')}</Typography>
									</Button>
								</Box>

							</Box>
							<Box className={classes.compareContent} ref={pdfBoxRef} style={{ alignItems: "center" }}>
								{(pdfFileUrl) &&
									<Document
										loading={<CircularProgress />}
										file={pdfFileUrl}
										onLoadError={loadErr}
										onLoadSuccess={loadSuccess}>
										{/*<Page pageNumber={page} renderTextLayer={false} renderAnnotationLayer={false} scale={1} loading/>*/}
										{pages && pages.map((item) => {
											return (
												<Page
													loading={<CircularProgress />}
													key={`page${item}`}
													className={classes.page}
													pageNumber={item}
													renderTextLayer={false}
													renderAnnotationLayer={false}
													scale={1}
													width={pdfWidth - 20}
												/>
											)
										})}
									</Document>
								}
							</Box>
						</Box>
						
						{
							// 2025.06.26 - 학생이면 아예 안 보이도록
						/*isStrokeRecognitionPossible == true ? 
							<CompareOcrDataStudent
								disabled={!handwritingConfirmed}
								handleClickClose={handleClickClose}
								projectCode={projectCode}
								templateProjectCode={templateProjectCode}
								submissionTransfer={submissionTransfer}
								pdfWithStroke={pdfWithStroke}
								ncodeRange={allocationRange}
								strokes={offlineStrokes}
							/> : null
						*/}
					</Box>
				</Box>
			</Box>

			{
				notAllocatedDlgState === "notAllocated" &&
				<NcodeIsNotAllocationDialog
					project={project}
					submissionTransfer={submissionTransfer}
					onClose={handleCloseNotAllocatedDlg}
				/>
			}
			
			<OfflineDataUploadDialog
				numPages={pageCount}
				projectCode={projectCode}
				templateProjectCode={templateProjectCode}
				submissionTransfer={submissionTransfer}
				handleClickBaseDialogClose={handleClickClose}
				type={projectExamStore.projectExamList[0]?.type || ProjectExamType.DESCRIPTIVE}
				openUploadDlg={openUploadDlg && !props.noShowPenDialog}
				setOfflineStrokes={setOfflineStrokes}
				setOpenUploadDlg={setOpenUploadDlg}
				setRequestOfflineDataState={setRequestOfflineDataState}
				requestOfflineDataState={requestOfflineDataState}
				ncodeRange={allocationRange}
			/>

			<Backdrop
				style={{ color: "#fff", zIndex: (theme as Theme).zIndex.modal + 1 }}
						open={isUSBModeUploadingData}>
				<CircularProgress color="inherit" />
			</Backdrop>

		</div >
	);
}


export default observer<typeof CompareNeoPenDataStudent>(CompareNeoPenDataStudent);
